import React, { useState } from 'react';
import { Heart, MessageSquare, Phone, ExternalLink, Star } from 'lucide-react';
import { apiClient } from '../../api/client';

/* Post-conversation reflection — 5 gold stars for the human professional. */
const HandoverStarRating = ({ interventionId }) => {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!stars || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.request('/api/empathic-handover/rate', {
        method: 'POST',
        body: JSON.stringify({ intervention_id: interventionId, stars, comment: comment.trim() || null }),
      });
      setDone(true);
    } catch (e) {
      setError("Couldn't save your rating — please try again.");
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="mt-1 p-3 bg-amber-50 border border-amber-200 rounded-xl text-center" data-testid="handover-rating-thanks">
        <p className="text-sm font-semibold text-amber-800">Thank you — your reflection helps us keep the best humans in your corner. 💛</p>
      </div>
    );
  }

  return (
    <div className="mt-1 p-3 bg-amber-50/60 border border-amber-200 rounded-xl" data-testid="handover-rating-block">
      <p className="text-xs font-semibold text-gray-700 text-center mb-2">
        When you're done — how was your conversation with the real human?
      </p>
      <div className="flex items-center justify-center gap-1.5 mb-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setStars(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
            data-testid={`handover-rating-star-${n}`}
            className="p-1 transition-transform hover:scale-125"
          >
            <Star
              size={26}
              fill={(hover || stars) >= n ? '#F5B301' : 'none'}
              stroke={(hover || stars) >= n ? '#F5B301' : '#C9C2B8'}
            />
          </button>
        ))}
      </div>
      {stars > 0 && (
        <>
          <input
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 500))}
            placeholder="Anything you'd like to add? (optional)"
            data-testid="handover-rating-comment"
            className="w-full px-3 py-2 text-xs border border-amber-200 rounded-lg mb-2 focus:outline-none focus:border-amber-400 bg-white"
          />
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="handover-rating-submit"
            className="w-full py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #F5B301, #F5831F)' }}
          >
            {submitting ? 'Saving…' : `Send my ${stars}-star reflection`}
          </button>
        </>
      )}
      {error && <p className="text-xs text-red-600 text-center mt-1" data-testid="handover-rating-error">{error}</p>}
    </div>
  );
};

/**
 * HandoverSessionBanner — renamed role: the Directed Referral Card.
 *
 * Shows a static, pre-approved AvA message and a primary action button that
 * deep-links to the user's SMS or phone app (sms: / tel: URI), so they start
 * the conversation with the trusted partner service themselves. HiBFF never
 * receives any of that conversation.
 *
 * Props:
 *   avaMessage           — static message pre-rendered by the backend
 *   primaryReferral      — { service_name, provider, channel, phone|short_code,
 *                            sms_keyword, sms_uri, tel_uri, hours }
 *   secondaryReferral    — optional second option (same shape)
 *   internationalDirectory (url)
 *   expert, farewell, session — legacy props preserved for graceful fallback
 */
const HandoverSessionBanner = ({
  avaMessage,
  primaryReferral,
  secondaryReferral,
  internationalDirectory,
  interventionId,
  expert,
  farewell,
  session,
}) => {
  // Back-compat: if the new referral payload isn't present but a session/expert
  // is, render nothing — we don't want the old "connected" banner any more.
  if (!avaMessage && !primaryReferral) {
    return null;
  }
  // Acknowledge unused legacy props without lint noise.
  void expert; void farewell; void session;

  const p = primaryReferral || {};
  const primaryButtonLabel =
    p.channel === 'text' || p.channel === 'call_or_text'
      ? `Text ${p.sms_keyword || ''} to ${p.short_code || p.phone}`
      : p.channel === 'call'
      ? `Call ${p.phone}`
      : 'Open directory';

  const primaryHref =
    p.sms_uri || p.tel_uri || p.url || internationalDirectory || '#';

  const PrimaryIcon =
    p.channel === 'text'
      ? MessageSquare
      : p.channel === 'call' || p.channel === 'call_or_text'
      ? Phone
      : ExternalLink;

  const SecondaryIcon =
    secondaryReferral?.channel === 'text'
      ? MessageSquare
      : secondaryReferral?.channel?.startsWith('call')
      ? Phone
      : ExternalLink;
  const secondaryHref =
    secondaryReferral?.sms_uri || secondaryReferral?.tel_uri || null;

  return (
    <div className="mx-2 sm:mx-4 my-2 sm:my-3" data-testid="handover-referral-card">
      {/* AvA static message */}
      <div className="p-3 sm:p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-t-xl border border-b-0 border-purple-200">
        <div className="flex items-start gap-2.5 sm:gap-3">
          <div className="w-7 h-7 sm:w-8 sm:h-8 bg-purple-500 rounded-full flex items-center justify-center text-white flex-shrink-0">
            <Heart size={14} />
          </div>
          <p
            className="text-sm text-purple-900 break-words leading-relaxed"
            data-testid="referral-ava-message"
          >
            {avaMessage}
          </p>
        </div>
      </div>

      {/* Primary action */}
      <div className="p-3 sm:p-4 bg-white rounded-b-xl border border-purple-200 flex flex-col gap-2.5">
        <a
          href={primaryHref}
          data-testid="referral-primary-action-btn"
          className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg text-sm"
        >
          <PrimaryIcon size={16} />
          {primaryButtonLabel}
        </a>

        {p.service_name && (
          <p className="text-xs text-gray-500 text-center">
            <span className="font-medium">{p.service_name}</span>
            {p.provider && p.provider !== p.service_name ? ` · ${p.provider}` : ''}
            {p.hours ? ` · ${p.hours}` : ''}
            {p.cost ? ` · ${p.cost}` : ''}
          </p>
        )}

        {secondaryReferral && secondaryHref && (
          <a
            href={secondaryHref}
            data-testid="referral-secondary-action-btn"
            className="flex items-center justify-center gap-2 w-full py-2.5 text-sm font-medium text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50"
          >
            <SecondaryIcon size={14} />
            Or {secondaryReferral.service_name}:{' '}
            {secondaryReferral.sms_keyword || ''}{' '}
            {secondaryReferral.sms_keyword ? 'to' : ''}{' '}
            {secondaryReferral.short_code || secondaryReferral.phone}
          </a>
        )}

        {internationalDirectory && (
          <a
            href={internationalDirectory}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-center text-gray-400 hover:text-gray-600 underline"
          >
            Not in the UK or US? Find your local helpline
          </a>
        )}

        {interventionId && <HandoverStarRating interventionId={interventionId} />}
      </div>
    </div>
  );
};

export default HandoverSessionBanner;
